import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import { formatNumberWithCommas } from "../../../utils/formate.utils";
import { ALL_SIGNUP_PLANS } from "../../../components/subscription/pricing/pricingData";
import { config } from "../../../config";
import chargebeeService from "../../../services/chargebee.service";
import { useSearchParams } from "react-router-dom";

interface Props {
  planId: string | null;
  onSubmit: (cardTokenResult: string) => Promise<void> | void;
  onSkip: () => void;
}

const SignupStepCard: React.FC<Props> = ({
  planId,
  onSubmit,
  onSkip,
}) => {
  const cardRef = useRef<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cardError, setCardError] = useState<string | null>(null);
  const [isCardValid, setIsCardValid] = useState(false);
  const [showPromoField, setShowPromoField] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [isYearly, setIsYearly] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isChargebeeReady, setIsChargebeeReady] = useState(false);
  const appliedPromo: any = null;

  const setPromoError = (_value: string) => {};
  const validateCoupon = () => {};

  useEffect(() => {
    let timerId: number | null = null;
    let isMounted = true;

    const checkReady = () => {
      if (!isMounted) {
        return;
      }

      try {
        const cb = (window as any).Chargebee;
        if (cb) {
          try {
            cb.getInstance();
            setIsChargebeeReady(true);
            return;
          } catch {
            chargebeeService.initChargebee(config.chargebee_site);
          }
        }
      } catch {}

      timerId = window.setTimeout(checkReady, 100);
    };

    checkReady();

    return () => {
      isMounted = false;
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }
    if (!isChargebeeReady) {
      setCardError("Payment form is not ready yet. Please try again.");
      return;
    }
    if (!cardRef.current?.tokenize) {
      setCardError("Instance not created");
      return;
    }

    setIsLoading(true);
    setCardError(null);

    try {
      const result = await cardRef.current.tokenize();
      if (result?.error) {
        setCardError(result.error.message || "Card tokenization failed");
        return;
      }

      const cardTokenResult = result?.token;
      if (!cardTokenResult) {
        setCardError("Please enter valid card details");
        return;
      }

      await onSubmit(cardTokenResult);
    } catch (error: any) {
      const message = error?.message || "Failed to process card details";
      if (
        message.includes("Cannot read properties of undefined (reading 'cancel')") ||
        message.includes("Instance not created")
      ) {
        setCardError("Payment form is not ready yet. Please try again.");
        return;
      }
      setCardError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAllInOnePricing = () => {
    const defaultPlanId = "All-In-One-USD-Yearly-200";
    const currentPlanId = planId ?? defaultPlanId;
    const plan =
      ALL_SIGNUP_PLANS[currentPlanId] ?? ALL_SIGNUP_PLANS[defaultPlanId];

    return {
      planName: plan.planName,
      monthlyPrice: plan.monthly,
      annualPlanPerMonth: plan.annualPlanPerMonth,
      annualMaxPrice: plan.annualMaxPrice,
      liveAttendees: plan.liveAttendees,
      automatedAttendees: plan.automatedAttendees,
      savings: plan.savingsAmount,
      savingsPercentage: plan.savingsAmount
        ? (plan.savingsAmount / plan.annualMaxPrice) * 100
        : 0,
      totalPrice: isYearly ? plan.annualPlanPerMonth * 12 : plan.monthly,
      addons: [],
    };
  };

  const calculateTotalPrice = () => {
    return calculateAllInOnePricing().totalPrice;
  };

  const updatePlanInUrl = (isYearly: boolean) => {
    if (!planId) return;

    // Extract the attendee number from the current planId
    const attendeeMatch = planId.match(/-(\d+)$/);
    const attendeeNumber = attendeeMatch ? attendeeMatch[1] : "200"; // Default to 250 if no number found

    // Extract the base plan ID without period and attendee number
    const basePlanId = planId
      .replace(/(Monthly|Yearly)/g, "")
      .replace(/-\d+$/, "");
    const period = isYearly ? "Yearly" : "Monthly";
    const newPlanId = `${basePlanId}${period}-${attendeeNumber}`;

    setSearchParams(
      (prev) => {
        prev.set("planId", newPlanId);
        return prev;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    updatePlanInUrl(isYearly);
  }, [isYearly]);
  

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", md: "center" },
        maxWidth: "1400px",
        minWidth: { xs: "100%", md: "920px" },
        mx: "auto",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          minWidth: { xs: "100%", md: "637px" },
          maxWidth: { xs: "100%", md: "50%" },
          bgcolor: { xs: "#fff", sm: "#fafafa" },
          px: { xs: 3, sm: 4, md: 6 },
          pt: { xs: 4, sm: 4, md: 6 },
          pb: { xs: 0, sm: 4, md: 6 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            mb: 3,
            display: { xs: "block", md: "none" },
          }}
        >
          <Typography
            color="#333333"
            fontWeight="700"
            fontSize="20px"
            mb="8px"
            lineHeight="1.2"
          >
            Start Your Full-Access Trial
          </Typography>
          <Box
            sx={{
              color: "#374151",
              fontSize: "15px",
              display: "inline-block",
            }}
          >
            After 7-Day Free Trial:{" "}
            {isYearly ? (
              <Box
                component="span"
                sx={{
                  textDecoration: "line-through",
                  fontSize: "16px",
                  color: "#9ca3af",
                }}
              >
                $
                {formatNumberWithCommas(
                  calculateAllInOnePricing().annualMaxPrice,
                )}
              </Box>
            ) : (
              ""
            )}{" "}
            <b>
              $
              {formatNumberWithCommas(
                isYearly
                  ? calculateAllInOnePricing().annualPlanPerMonth
                  : calculateTotalPrice(),
              )}
              /month {isYearly ? "(billed annually)" : ""}
            </b>
            , cancel anytime
          </Box>
        </Box>
        <Box mb={{ xs: 4, sm: 5 }}>
          <Typography
            fontSize={{ xs: "20px", sm: "24px" }}
            fontWeight={700}
            mb={{ xs: 2, sm: 3 }}
            color="#000"
          >
            {calculateAllInOnePricing().planName} Plan
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mb={{ xs: 2, sm: 2.5 }}
            flexWrap="wrap"
          >
            <Box
              display="flex"
              alignItems="center"
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "100px",
                overflow: "visible",
                p: 0.5,
                background: "#F3F4F6",
                width: "fit-content",
                position: "relative",
              }}
            >
              <Box
                onClick={() => setIsYearly(false)}
                sx={{
                  px: { xs: 2.5, sm: 3.5 },
                  py: { xs: 1, sm: 1.25 },
                  background: !isYearly ? "#fff" : "transparent",
                  color: !isYearly ? "#000" : "#6B7280",
                  borderRadius: "100px",
                  fontWeight: !isYearly ? 600 : 400,
                  fontSize: { xs: "13px", sm: "14px" },
                  cursor: "pointer",
                  boxShadow: !isYearly ? "0 1px 3px 0 rgba(0, 0, 0, 0.1)" : "none",
                  border: "none",
                  transition: "all 0.2s",
                  zIndex: !isYearly ? 2 : 1,
                  display: "flex",
                  alignItems: "center",
                  minWidth: { xs: "80px", sm: "90px" },
                  justifyContent: "center",
                }}
              >
                Monthly
              </Box>
              <Box
                onClick={() => setIsYearly(true)}
                sx={{
                  px: { xs: 2.5, sm: 3.5 },
                  py: { xs: 1, sm: 1.25 },
                  background: isYearly ? "#000" : "transparent",
                  color: isYearly ? "#fff" : "#6B7280",
                  borderRadius: "100px",
                  fontWeight: isYearly ? 600 : 400,
                  fontSize: { xs: "13px", sm: "14px" },
                  cursor: "pointer",
                  boxShadow: isYearly ? "0 1px 3px 0 rgba(0, 0, 0, 0.1)" : "none",
                  border: "none",
                  transition: "all 0.2s",
                  zIndex: isYearly ? 2 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: { xs: "80px", sm: "90px" },
                  justifyContent: "center",
                }}
              >
                Annual
                  <Box
                    sx={{
                      bgcolor: "#16A34A",
                      color: "#fff",
                      fontSize: "9px",
                      fontWeight: 600,
                      px: 0.75,
                      py: 0.35,
                      borderRadius: "4px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Best Value
                  </Box>
                {/* {isYearly && (
                )} */}
              </Box>
            </Box>
            {isYearly && (
              <Box
                sx={{
                  backgroundColor: "#16A34A",
                  color: "#fff",
                  fontSize: { xs: "12px", sm: "13px" },
                  fontWeight: 500,
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 0.75, sm: 1 },
                  borderRadius: "20px",
                }}
              >
                You save $
                {formatNumberWithCommas(calculateAllInOnePricing().savings)}
                /year
              </Box>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{ color: "#131927", fontSize: "14px", mb: 3 }}
          >
            7-day free trial then billed {isYearly ? "annually" : "monthly"}
          </Typography>
          <Box
            sx={{
              bgcolor: "#F3F4F6",
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              mt: 4,
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #d4d4d4fa",
                pb: { xs: 2, sm: 3 },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography fontSize="16px" fontWeight={400} color="#000">
                  {calculateAllInOnePricing().planName} Plan (
                  {isYearly ? "Annual" : "Monthly"})
                </Typography>
                <Typography fontSize="18px" fontWeight={600} color="#000">
                  $
                  {formatNumberWithCommas(
                    isYearly
                      ? calculateAllInOnePricing().annualPlanPerMonth * 12
                      : calculateAllInOnePricing().monthlyPrice,
                  )}
                  /{isYearly ? "yr" : "mo"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ pt: { xs: 2, sm: 3 } }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={1}
              >
                <Typography fontSize="16px" fontWeight={400} color="#000">
                  Due today
                </Typography>
                <Typography
                  fontSize={{ xs: "24px", sm: "28px" }}
                  fontWeight={600}
                  color="#000"
                  sx={{ lineHeight: 1 }}
                >
                  $0.00
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#999",
                  fontSize: "13px",
                  textAlign: "right",
                  letterSpacing: "0.05em",
                }}
              >
                $
                {formatNumberWithCommas(
                  isYearly
                    ? calculateAllInOnePricing().annualPlanPerMonth * 12
                    : calculateAllInOnePricing().monthlyPrice,
                )}{" "}
                billed after trial
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            my: 2,
          }}
        >
          {[
            "No platform fees — keep 100% of what you make",
            "7-day full access, cancel anytime — no strings attached",
            "Webinars, CRM, Al & checkout — one platform, zero duct tape",
          ].map((feature) => (
            <Box
              key={feature}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: "#222",
                    fontSize: "14px",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  ✓
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: "#333",
                  fontSize: { xs: "12px", md: "14px" },
                  fontWeight: 400,
                  lineHeight: 1.35,
                }}
              >
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ p: { xs: 3, sm: 4, md: 6 }, width: { xs: "100%", md: "50%" } }}>
        <Box sx={{ border: "1px solid #e5e7eb", mb: 3, borderRadius: "12px", p: { xs: 3, sm: 4, md: 5 }, bgcolor: "#fff" }}>
          <Typography
            color="#333333"
            fontWeight="700"
            fontSize={{ xs: "20px", sm: "24px", lg: "28px" }}
            mb="8px"
            lineHeight="1.2"
            sx={{ display: { xs: "none", md: "block" } }}
          >
            Start Your Full-Access Trial
          </Typography>
          <Box
            sx={{
              color: "#374151",
              fontSize: { xs: "13px", sm: "15px" },
              display: { xs: "none", md: "inline-block" },
              mb: 2,
            }}
          >
            After 7-Day Free Trial:{" "}
            {isYearly ? (
              <Box
                component="span"
                sx={{
                  textDecoration: "line-through",
                  fontSize: { xs: "14px", sm: "16px" },
                  color: "#9ca3af",
                }}
              >
                $
                {formatNumberWithCommas(
                  calculateAllInOnePricing().annualMaxPrice,
                )}
              </Box>
            ) : (
              ""
            )}{" "}
            <b>
              $
              {formatNumberWithCommas(
                isYearly
                  ? calculateAllInOnePricing().annualPlanPerMonth
                  : calculateTotalPrice(),
              )}
              /month {isYearly ? "(billed annually)" : ""}
            </b>
            , cancel anytime
          </Box>
          <Box sx={{ borderBottom: "1px solid #e5e7eb", mb: { xs: 3, md: 4 }, display: { xs: "none", md: "block" } }} />
          <Box width="100%" mb={{ xs: 2, sm: 3 }}>
            <Typography fontSize={{ xs: "12px", sm: "13px" }} mb={0.5} fontWeight={600} color="#374151">
              Card Details
            </Typography>
            <Box
              width="100%"
              sx={{
                position: "relative",
                "& .CbHosted": {
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  padding: "14px 16px",
                  "&:hover, &--focus": { borderColor: "#666666" },
                },
              }}
            >
              {isChargebeeReady ? (
                <CardComponent
                  ref={cardRef}
                  currency="USD"
                  showTestCards={true}
                  styles={{
                    border: "1px solid #e5e7eb",
                    base: {
                      padding: "14px 16px",
                      border: "1px solid #e5e7eb",
                      fontSize: "14px",
                      fontFamily: "Inter, sans-serif",
                      color: "#333333",
                      "::placeholder": { color: "#9ca3af" },
                    },
                    invalid: { color: "#ff0000" },
                  }}
                  onChange={(status: any) => {
                    if (status.error) {
                      setCardError(status.error.message);
                      setIsCardValid(false);
                    } else if (!status.complete) {
                      setCardError(null);
                      setIsCardValid(false);
                    } else {
                      setCardError(null);
                      setIsCardValid(true);
                    }
                  }}
                  onReady={() => {
                    setIsCardValid(false);
                  }}
                />
              ) : (
                <Box
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    padding: "14px 16px",
                    fontSize: "14px",
                    color: "#9ca3af",
                  }}
                >
                  Loading payment form...
                </Box>
              )}
            </Box>
            {cardError && (
              <Typography color="error" fontSize="12px" mt={1}>
                {cardError}
              </Typography>
            )}
          </Box>

          <Box display="flex" justifyContent="end" flexDirection="column">
            {!showPromoField ? (
              <>
                {appliedPromo ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "4px 8px",
                      backgroundColor: "#F0FFF4",
                      borderRadius: "4px",
                      width: "fit-content",
                      marginLeft: "auto",
                    }}
                  >
                    <Typography color="#1D9652" fontSize="14px">
                      {appliedPromo.code} applied
                    </Typography>
                  </Box>
                ) : null}
              </>
            ) : (
              <TextField
                variant="outlined"
                placeholder="Promo Code:"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setPromoError("");
                }}
                error={false}
                helperText={""}
                sx={(theme) => ({
                  width: "100%",
                  marginBottom: "16px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "14px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e5e7eb",
                  },
                  [theme.breakpoints.down("sm")]: {
                    "& .MuiOutlinedInput-input": {
                      height: "1em",
                      padding: "14px 14px",
                    },
                  },
                })}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ paddingRight: "10px" }}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              background: "#667EEA",
                              borderRadius: "4px",
                              padding: "4px 10px",
                              marginRight: "4px",
                              minWidth: "57px",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#fff",
                              cursor: "pointer",
                            }}
                            onClick={validateCoupon}
                          >
                            Apply
                          </Box>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="17"
                            height="16"
                            viewBox="0 0 17 16"
                            fill="none"
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowPromoField(false)}
                          >
                            <path
                              d="M8.07324 1C4.17324 1 1.07324 4.1 1.07324 8C1.07324 11.9 4.17324 15 8.07324 15C11.9732 15 15.0732 11.9 15.0732 8C15.0732 4.1 11.9732 1 8.07324 1ZM10.7732 11.5L8.07324 8.8L5.37324 11.5L4.57324 10.7L7.27324 8L4.57324 5.3L5.37324 4.5L8.07324 7.2L10.7732 4.5L11.5732 5.3L8.87324 8L11.5732 10.7L10.7732 11.5Z"
                              fill="#A3A3A3"
                            />
                          </svg>
                        </Box>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || !isCardValid}
            disableElevation
            fullWidth
            sx={{
              mt: { xs: 2.5, sm: 3 },
              py: { xs: 1.2, sm: 1.6 },
              borderRadius: "8px",
              color: "#fff !important",
              fontWeight: 600,
              fontSize: { xs: "14px", sm: "16px" },
              textTransform: "none",
              "&.Mui-disabled": {
                bgcolor: "#cccccc !important",
                color: "#666666 !important",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : !isCardValid ? (
              "Please complete card details"
            ) : (
              "Start My Free Trial"
            )}
          </Button>
          {/* <Button
            variant="text"
            onClick={onSkip}
            disabled={isLoading || isAutoRegistering}
            fullWidth
            sx={{ mt: 1, textTransform: "none", color: "#667EEA" }}
          >
            Skip and continue without card
          </Button> */}
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Typography
              fontSize={{ xs: "12px", sm: "14px" }}
              color="#9ca3af"
              textAlign="center"
              lineHeight={1.6}
            >
              By starting your trial, you agree to our{" "}
              <a
                href="https://easywebinar.com/terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#667EEA",
                }}
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://easywebinar.com/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#667EEA",
                }}
              >
                Privacy Policy
              </a>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignupStepCard;
